import { useState, useCallback, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import type {
  CalendarShareDetail,
  CalendarShareInput,
  CalendarShareUpdate,
  SharedCalendarSummary,
} from '../types';

interface UseCalendarSharingOptions {
  calendarId: string | null;
}

export function useCalendarSharing({ calendarId }: UseCalendarSharingOptions) {
  const [shares, setShares] = useState<CalendarShareDetail[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchShares = useCallback(async () => {
    if (!calendarId) return;
    setLoading(true);
    try {
      const { shares: data } = await api.listCalendarShares(calendarId);
      // Enrich each row with its grantee's username via the batch
      // reverse-lookup. Best-effort: any holder that can't be resolved
      // (unknown, non-personal, missing principal/user) stays
      // username-less and renders as the truncated holder ID.
      const granteeHolderIds = Array.from(
        new Set(data.map((s) => s.granteeHolderId).filter(Boolean)),
      );
      let enriched = data;
      if (granteeHolderIds.length > 0) {
        try {
          const { users } = await api.resolveCalendarShareHolders(granteeHolderIds);
          const usernameByHolder = new Map(users.map((u) => [u.holderId, u.username]));
          enriched = data.map((s) => {
            // Don't overwrite an already-resolved username (the create
            // flow echoes it back from the search step).
            if (s.granteeUsername) return s;
            const u = usernameByHolder.get(s.granteeHolderId);
            return u ? { ...s, granteeUsername: u } : s;
          });
        } catch (err) {
          console.warn('[calendar sharing] Username reverse-lookup failed', err);
        }
      }
      setShares(enriched);
    } catch (err) {
      console.error('[calendar sharing] Failed to fetch shares', err);
      toast({
        title: 'Failed to load shares',
        description: err instanceof Error ? err.message : 'Could not load calendar shares',
        variant: 'destructive',
      });
      setShares([]);
    } finally {
      setLoading(false);
    }
  }, [calendarId]);

  useEffect(() => {
    fetchShares();
  }, [fetchShares]);

  const createShare = useCallback(
    async (input: CalendarShareInput) => {
      if (!calendarId) throw new Error('No calendar selected');
      const share = await api.createCalendarShare(calendarId, input);
      setShares((prev) => [...prev, share]);
      return share;
    },
    [calendarId],
  );

  const updateShare = useCallback(
    async (consentId: string, input: CalendarShareUpdate) => {
      if (!calendarId) throw new Error('No calendar selected');
      const updated = await api.updateCalendarShare(calendarId, consentId, input);
      setShares((prev) => prev.map((s) => (s.consentId === consentId ? updated : s)));
      return updated;
    },
    [calendarId],
  );

  const deleteShare = useCallback(
    async (consentId: string) => {
      if (!calendarId) throw new Error('No calendar selected');
      await api.deleteCalendarShare(calendarId, consentId);
      setShares((prev) => prev.filter((s) => s.consentId !== consentId));
    },
    [calendarId],
  );

  return {
    shares,
    loading,
    fetchShares,
    createShare,
    updateShare,
    deleteShare,
  };
}

export function useSharedCalendars() {
  const [sharedCalendars, setSharedCalendars] = useState<SharedCalendarSummary[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchSharedCalendars = useCallback(async () => {
    setLoading(true);
    try {
      const { sharedCalendars: data } = await api.listSharedCalendars();
      setSharedCalendars(data);
    } catch (err) {
      console.error('[calendar sharing] Failed to fetch shared calendars', err);
      setSharedCalendars([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSharedCalendars();
  }, [fetchSharedCalendars]);

  return { sharedCalendars, loading, fetchSharedCalendars };
}
